import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  PLATFORM_ID,
  Inject,
  Input,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import {
  FormsModule,
  FormGroup,
  Validators,
  FormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { interval, Subscription, take } from 'rxjs';
import { AuthService } from '../../shared/services/auth.service';
import { Router } from '@angular/router';
import { secureStorage } from '../../shared/services/secure-storage.service';
import { RootService } from '../../shared/services/root.service';
import { CookieService } from '../../shared/services/cookie.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  BusinessStepResult,
  RegisterComponent,
} from '../register/register.component';
import { TokenService } from '../../shared/services/token.service';
import { BuyerVerificationService } from '../../shared/services/buyer-verification.service';
import {
  GstinItem,
  INDIAN_SHOP_LICENSE_STATES,
  VerificationStatusState,
} from '../../shared/models/buyer-verification.models';
import { CartService } from '../../shared/services/cart.service';
import { WishlistStorageService } from '../../shared/services/wishlist-storage.service';
import { ApiService } from '../../shared/services/api.service';
import { NgOtpInputModule } from 'ng-otp-input';
import { environment } from '../../../environment/environment';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RegisterComponent,
    NgOtpInputModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() productData: any;
  @Input() actionType: string = '';
  /** Opens directly to buyer verification (checkout resume). */
  @Input() verificationOnly = false;
  @Input() verificationCheckoutMode = false;
  @Output() loginSuccess = new EventEmitter<void>();
  @Output() verificationComplete = new EventEmitter<void>();

  verifyOtp = false;
  createForm = false;
  /** Post-registration steps managed in login: gstin | shop-license | status | activated */
  verificationStep: 'gstin' | 'shop-license' | 'status' | 'activated' | null =
    null;
  registerBusinessOnly = false;
  showLoginModal = true;

  // GSTIN selection
  gstins: GstinItem[] = [];
  selectedGstin: GstinItem | null = null;
  gstinLoading = false;
  gstinErrorMessage = '';
  private linkedDocuments: GstinItem[] = [];

  // Shop license / UAM verification (when no GSTINs linked to PAN)
  readonly shopLicenseStates = INDIAN_SHOP_LICENSE_STATES;
  readonly shopLicenseTypeOptions = [
    { value: 'uam' as const, label: 'UAM Number' },
    { value: 'certificate' as const, label: 'Certificate Number' },
  ];
  shopLicenseType: 'uam' | 'certificate' = 'certificate';
  udyamItems: GstinItem[] = [];
  uamNumber = '';
  uamLookupLoading = false;
  uamLookupErrorMessage = '';
  shopLicenseNumber = '';
  shopLicenseState = '';
  shopLicenseCommencementDate = '';
  shopLicenseDistrict = '';
  /** Prefilled from PAN step; sent with certificate verification. */
  shopLicenseName = '';
  shopLicenseLoading = false;
  shopLicenseErrorMessage = '';
  uamNumberTouched = false;
  shopLicenseNumberTouched = false;
  shopLicenseStateTouched = false;
  shopLicenseCommencementDateTouched = false;
  shopLicenseDistrictTouched = false;
  verificationRetryStep: 'gstin' | 'shop-license' | 'business' = 'business';

  // Verification status
  verificationStatusState: VerificationStatusState = 'verifying';
  verificationStatusMessage = '';
  legalName = '';
  legalAddress = '';
  otpForm: FormGroup;
  loginForm: FormGroup;
  otpControls = Array.from({ length: 6 }, (_, i) => `digit${i}`);
  phoneNumber = '';
  resendText = 'Resend OTP in 00:59';
  errorMessage = '';
  loading = false;
  private timerSubscription?: Subscription;
  remainingSeconds = 59;
  private sub: Subscription = new Subscription();
  mobileOtpValue: string = ''; // For ng-otp-input in mobile view
  hasOtpError: boolean = false; // Track if OTP has error to keep red border
  turnstileWidgetId: string | null = null; // Store Turnstile widget ID
  turnstileToken: string | null = null; // Store Turnstile token
  private readonly TURNSTILE_SITE_KEY = environment.TURNSTILE_SITE_KEY;

  // Base configuration for ng-otp-input (mobile view)
  private baseOtpConfig = {
    length: 6,
    allowNumbersOnly: true,
    inputStyles: {
      width: '48px',
      height: '48px',
      border: '1px solid #9ca3af',
      'border-radius': '8px',
      'font-size': '18px',
      'font-weight': '600',
      'text-align': 'center',
    },
    containerClass: 'flex gap-2 justify-start',
    inputClass: 'focus:outline-none focus:border-blue-500',
  };
  loginbypass: any;

  // Getter for OTP config that dynamically updates border color based on error state
  get otpConfig() {
    return {
      ...this.baseOtpConfig,
      inputStyles: {
        ...this.baseOtpConfig.inputStyles,
        border: this.hasOtpError ? '2px solid #ef4444' : '1px solid #9ca3af',
      },
    };
  }

  // Method to get OTP config (creates new object reference each time for reactivity)
  getOtpConfig() {
    return {
      ...this.baseOtpConfig,
      inputStyles: {
        ...this.baseOtpConfig.inputStyles,
        border: this.hasOtpError ? '2px solid #ef4444' : '1px solid #9ca3af',
      },
    };
  }

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  @ViewChild('mobileNumberInput') mobileNumberInput!: ElementRef;
  @ViewChild('mobileNumberInputMobile') mobileNumberInputMobile!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private localstorage: secureStorage,
    private storageService: RootService,
    private ssrcookieService: CookieService,
    private tokenService: TokenService,
    private cartService: CartService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private wishlistStorageService: WishlistStorageService,
    private apiService: ApiService,
    private buyerVerificationService: BuyerVerificationService,
    private modalService: ModalService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.loginForm = this.fb.group({
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^[6-9][0-9]{9}$')],
      ],
      // termsAccepted: [false, [Validators.requiredTrue]]
    });

    this.otpForm = this.fb.group({
      digit0: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit1: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit2: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit3: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit4: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
      digit5: ['', [Validators.required, Validators.pattern('^[0-9]$')]],
    });
  }

  isMobile: boolean = false;
  ngOnInit(): void {
    this.modalService.openModal('login-popup', () => this.closeModal());
    if (this.verificationOnly) {
      this.showLoginModal = false;
      this.verifyOtp = false;
      this.loadVerificationStatus();
    }
    this.startResendTimer();
    if (isPlatformBrowser(this.platformId)) {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ) &&
        window.innerWidth < 480
      ) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    }
  }

  ngAfterViewInit(): void {
    // Initialize Turnstile
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTurnstile();
    }

    // Focus the mobile number input when view initializes
    // Try multiple times with delays to handle conditional rendering
    if (isPlatformBrowser(this.platformId)) {
      const attempts = [100, 300, 500, 700];
      attempts.forEach((delay) => {
        setTimeout(() => {
          // Check if mobile/tablet view is active (< 1024px)
          const isMobileView = window.innerWidth < 1024 || this.isMobile;

          // Try desktop view input
          if (this.showLoginModal && !isMobileView) {
            if (this.mobileNumberInput?.nativeElement) {
              try {
                this.mobileNumberInput.nativeElement.focus();
                return; // Successfully focused, exit
              } catch (e) {
                // Element might not be focusable
              }
            }
            // Fallback: Try direct DOM query for desktop
            const desktopInput = document.getElementById(
              'mobileNumber',
            ) as HTMLInputElement;
            if (desktopInput) {
              try {
                desktopInput.focus();
                return;
              } catch (e) {
                // Element might not be focusable
              }
            }
          }

          // Try mobile view input
          if (!this.verifyOtp && isMobileView) {
            if (this.mobileNumberInputMobile?.nativeElement) {
              try {
                this.mobileNumberInputMobile.nativeElement.focus();
                return; // Successfully focused, exit
              } catch (e) {
                // Element might not be focusable
              }
            }
            // Fallback: Try direct DOM query for mobile
            const mobileInput = document.getElementById(
              'mobileNumberMobile',
            ) as HTMLInputElement;
            if (mobileInput) {
              try {
                mobileInput.focus();
                return;
              } catch (e) {
                // Element might not be focusable
              }
            }
          }
        }, delay);
      });

      // Set margin-left for OTP input wrapper
      // setTimeout(() => {
      //   const otpWrapper = document.querySelector('ng-otp-input .ng-otp-input-wrapper') as HTMLElement;
      //   if (otpWrapper) {
      //     otpWrapper.style.marginLeft = '-2rem';
      //   }
      // }, 100);
    }
  }

  ngOnDestroy() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.modalService.closeModal('login-popup');
    if (this.sub) {
      this.sub.unsubscribe();
    }
    // Reset Turnstile widget
    if (isPlatformBrowser(this.platformId) && this.turnstileWidgetId !== null) {
      const turnstile = (window as any).turnstile;
      if (turnstile) {
        try {
          turnstile.remove(this.turnstileWidgetId);
        } catch (e) {
          console.error('Error removing Turnstile widget:', e);
        }
      }
    }
  }

  initializeTurnstile(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const turnstile = (window as any).turnstile;
    if (!turnstile) {
      // Retry if Turnstile script hasn't loaded yet
      setTimeout(() => this.initializeTurnstile(), 100);
      return;
    }

    // Remove existing widget if any
    if (this.turnstileWidgetId !== null) {
      try {
        turnstile.remove(this.turnstileWidgetId);
      } catch (e) {
        // Ignore errors if widget doesn't exist
      }
      this.turnstileWidgetId = null;
      // Reset token when removing widget
      this.ngZone.run(() => {
        this.turnstileToken = null;
        this.cdr.detectChanges();
      });
    }

    // Wait for the container to be available
    // Use multiple retries with increasing delays to handle conditional rendering
    const attemptInit = (attempt: number = 1, maxAttempts: number = 10) => {
      setTimeout(
        () => {
          // Determine which container to use based on mobile/tablet/desktop view
          // Treat tablet (< 1024px) as mobile view for Turnstile
          const isMobileView = window.innerWidth < 1024 || this.isMobile;
          let container: HTMLElement | null = null;

          // Check if we're in mobile/tablet view - prioritize mobile container
          if (isMobileView) {
            container = document.getElementById('turnstile-container-mobile');
            // If mobile container not found, fallback to desktop
            if (!container) {
              container = document.getElementById('turnstile-container');
            }
          } else {
            // Desktop view - try desktop container first
            container = document.getElementById('turnstile-container');
            // If desktop container not found, fallback to mobile
            if (!container) {
              container = document.getElementById('turnstile-container-mobile');
            }
          }

          // Additional check: ensure container is visible (not hidden by CSS)
          if (container) {
            const style = window.getComputedStyle(container);
            const isVisible =
              style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              container.offsetParent !== null;

            // If container exists but is not visible, try the other one
            if (!isVisible) {
              const alternateContainer =
                container.id === 'turnstile-container-mobile'
                  ? document.getElementById('turnstile-container')
                  : document.getElementById('turnstile-container-mobile');

              if (alternateContainer) {
                const altStyle = window.getComputedStyle(alternateContainer);
                const altIsVisible =
                  altStyle.display !== 'none' &&
                  altStyle.visibility !== 'hidden' &&
                  alternateContainer.offsetParent !== null;
                if (altIsVisible) {
                  container = alternateContainer;
                } else {
                  container = null; // Neither container is visible
                }
              } else {
                container = null; // Alternate container doesn't exist
              }
            }
          }

          if (container) {
            try {
              this.turnstileWidgetId = turnstile.render(container, {
                sitekey: this.TURNSTILE_SITE_KEY,
                callback: (token: string) => {
                  this.ngZone.run(() => {
                    this.turnstileToken = token;
                    this.cdr.detectChanges();
                  });
                },
                'error-callback': () => {
                  this.ngZone.run(() => {
                    this.turnstileToken = null;
                    this.cdr.detectChanges();
                  });
                },
                'expired-callback': () => {
                  this.ngZone.run(() => {
                    this.turnstileToken = null;
                    this.cdr.detectChanges();
                  });
                },
              });
            } catch (e) {
              console.error('Error initializing Turnstile:', e);
              // Retry on error
              if (attempt < maxAttempts) {
                attemptInit(attempt + 1, maxAttempts);
              }
            }
          } else {
            // Retry if container not found yet (might be rendered later)
            if (attempt < maxAttempts) {
              attemptInit(attempt + 1, maxAttempts);
            } else {
              console.warn(
                'Turnstile container not found after multiple attempts',
              );
            }
          }
        },
        attempt === 1 ? 300 : attempt * 100,
      ); // Initial delay 300ms, then increasing delays
    };

    attemptInit();
  }

  resetTurnstile(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const turnstile = (window as any).turnstile;
    if (turnstile && this.turnstileWidgetId !== null) {
      try {
        turnstile.reset(this.turnstileWidgetId);
        this.ngZone.run(() => {
          this.turnstileToken = null;
          this.cdr.detectChanges();
        });
      } catch (e) {
        console.error('Error resetting Turnstile:', e);
      }
    } else {
      // If no widget exists, still reset the token and trigger change detection
      this.ngZone.run(() => {
        this.turnstileToken = null;
        this.cdr.detectChanges();
      });
    }
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Clear error message when user starts typing in OTP field
    if (this.errorMessage) {
      this.errorMessage = '';
    }

    // Handle paste events
    if (value.length > 1) {
      const digits = value.replace(/[^0-9]/g, '').split('');
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          this.otpForm.get(this.otpControls[index + i])?.setValue(digit);
          if (index + i + 1 < 6) {
            setTimeout(() => {
              this.otpInputs.get(index + i + 1)?.nativeElement.focus();
            }, 0);
          }
        }
      });
      return;
    }

    // Handle single digit input
    value = value.replace(/[^0-9]/g, '');
    this.otpForm.get(this.otpControls[index])?.setValue(value);

    if (value && index < 5) {
      setTimeout(() => {
        this.otpInputs.get(index + 1)?.nativeElement.focus();
      }, 0);
    }

    // Check if all digits are filled to auto-submit
    // if (this.otpForm.valid) {
    //   this.verifyOtpSubmit();
    // }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = this.otpForm.get(this.otpControls[index])?.value;

    // Clear error message when user presses backspace to clear OTP field
    if (event.key === 'Backspace' && this.errorMessage) {
      this.errorMessage = '';
    }

    if (event.key === 'Backspace' && !input && index > 0) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }

    // Handle Enter key press
    if (event.key === 'Enter' && this.otpForm.valid && !this.loading) {
      event.preventDefault();
      this.verifyOtpSubmit();
    }
  }

  private startResendTimer(): void {
    this.remainingSeconds = 59;
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    // Use setInterval inside NgZone to ensure change detection works
    const timer = setInterval(() => {
      this.ngZone.run(() => {
        this.remainingSeconds--;
        const minutes = Math.floor(this.remainingSeconds / 60);
        const seconds = this.remainingSeconds % 60;
        this.resendText = `Resend OTP in ${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        if (this.remainingSeconds === 0) {
          this.resendText = 'Resend OTP';
          clearInterval(timer);
        }
        // Ensure UI updates immediately on each tick
        this.cdr.detectChanges();
      });
    }, 1000);

    // Store the timer reference for cleanup
    this.timerSubscription = new Subscription(() => {
      clearInterval(timer);
    });
  }

  getOtp(skipCaptchaCheck: boolean = false) {
    // Mark the field as touched to trigger validation
    this.loginForm.get('phoneNumber')?.markAsTouched();

    // Check if the form is valid and phone number is 10 digits
    if (
      this.loginForm.get('phoneNumber')?.valid &&
      this.loginForm.get('phoneNumber')?.value?.length === 10 &&
      !this.loading
    ) {
      // Check if Turnstile token is available (skip check when resending OTP)
      if (!skipCaptchaCheck && !this.turnstileToken) {
        this.errorMessage = 'Please complete the captcha verification.';
        this.cdr.detectChanges();
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      const phoneNumber = this.loginForm.get('phoneNumber')?.value;
      // When resending OTP, skip captcha verification (pass undefined)
      const captchaToken = skipCaptchaCheck
        ? 'is_resend_otp'
        : this.turnstileToken || undefined;

      this.sub.add(
        this.authService.sendOtp(phoneNumber, captchaToken).subscribe({
          next: (response: any) => {
            this.ngZone.run(() => {
              this.loading = false;
              this.verifyOtp = true;
              this.showLoginModal = false;
              this.createForm = false;
              this.phoneNumber = phoneNumber;
              localStorage.setItem('registrationPhoneNumber', this.phoneNumber);

              this.otpForm.reset();
              this.mobileOtpValue = ''; // Reset mobile OTP value
              this.hasOtpError = false; // Reset error flag
              this.resetTurnstile(); // Reset Turnstile after successful OTP send
              this.startResendTimer();
              this.cdr.detectChanges();
              if (!this.isMobile) {
                setTimeout(() => {
                  this.otpInputs.first?.nativeElement.focus();
                }, 0);
              }

              // Set margin-left for OTP input wrapper (mobile view)
              // if (this.isMobile) {
              //   setTimeout(() => {
              //     const otpWrapper = document.querySelector('ng-otp-input .ng-otp-input-wrapper') as HTMLElement;
              //     if (otpWrapper) {
              //       otpWrapper.style.marginLeft = '-2rem';
              //     }
              //   }, 200);
              // }
            });
          },
          error: (error: any) => {
            this.ngZone.run(() => {
              this.loading = false;
              this.errorMessage =
                error.message || 'Failed to send OTP. Please try again.';
              this.cdr.detectChanges();
            });
          },
        }),
      );
    }
  }

  onEditClick() {
    this.createForm = false;
    this.verifyOtp = false;
    this.showLoginModal = true;
    this.errorMessage = '';
    this.otpForm.reset();
    this.mobileOtpValue = ''; // Reset mobile OTP value
    this.hasOtpError = false; // Reset error flag
    this.resetTurnstile(); // Reset Turnstile when going back
    // Reinitialize Turnstile after a delay to ensure container is rendered
    // Use longer delay for mobile view to account for conditional rendering
    const delay = this.isMobile ? 400 : 200;
    setTimeout(() => {
      this.initializeTurnstile();
    }, delay);

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.cdr.detectChanges();
    // Focus the mobile number input when returning to login
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        // Try desktop view input
        if (this.mobileNumberInput?.nativeElement && !this.isMobile) {
          try {
            this.mobileNumberInput.nativeElement.focus();
          } catch (e) {
            // Element might not be focusable
          }
        }
        // Try mobile view input
        if (this.mobileNumberInputMobile?.nativeElement && this.isMobile) {
          try {
            this.mobileNumberInputMobile.nativeElement.focus();
          } catch (e) {
            // Element might not be focusable
          }
        }
        // Fallback: Try direct DOM query
        if (this.isMobile) {
          const mobileInput = document.getElementById(
            'mobileNumberMobile',
          ) as HTMLInputElement;
          if (mobileInput) {
            try {
              mobileInput.focus();
            } catch (e) {
              // Element might not be focusable
            }
          }
        } else {
          const desktopInput = document.getElementById(
            'mobileNumber',
          ) as HTMLInputElement;
          if (desktopInput) {
            try {
              desktopInput.focus();
            } catch (e) {
              // Element might not be focusable
            }
          }
        }
      }, 200);
    }
  }

  // Handler for ng-otp-input onOtpChange event (mobile view only)
  onOtpChange(otp: string): void {
    this.mobileOtpValue = otp || ''; // Ensure it's always a string, never null/undefined

    // If error exists, keep red border visible even when removing digits
    // The getOtpConfig() method will return red border when hasOtpError is true
    if (this.hasOtpError) {
      // Force change detection to ensure red border stays visible
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }

    // If user enters 6 digits and there was an error, clear the error state
    if (
      this.mobileOtpValue &&
      this.mobileOtpValue.length === 6 &&
      this.hasOtpError
    ) {
      this.hasOtpError = false;
      this.errorMessage = '';
      this.cdr.detectChanges(); // Force change detection to update border color
    }

    // Auto-verify when 6 digits are entered
    if (this.mobileOtpValue && this.mobileOtpValue.length === 6) {
      // Optional: Auto-submit can be enabled here if needed
      // this.verifyOtpSubmit();
    }
  }

  verifyOtpSubmit() {
    // Get OTP value - use mobileOtpValue for mobile view, otherwise use form controls
    let otp = '';
    if (
      this.isMobile &&
      this.mobileOtpValue &&
      this.mobileOtpValue.length === 6
    ) {
      otp = this.mobileOtpValue;
    } else if (this.otpForm.valid) {
      otp = this.otpControls
        .map((control) => this.otpForm.get(control)?.value)
        .join('');
    }

    if (otp.length === 6 && !this.loading) {
      this.loading = true;
      this.errorMessage = '';
      localStorage.setItem('registrationPhoneNumber', this.phoneNumber);
      localStorage.setItem('registrationOtp', otp);

      this.sub.add(
        this.authService
          .verifyOtp(this.phoneNumber, otp, this.productData)
          .subscribe({
            next: (response: any) => {
              this.loading = false;

              this.loginbypass = response.data;
              if (response.success) {
                // if(response?.data?.uuid){
                //   this.proceedWithLoginBypass(response?.data?.uuid);
                // }

                if (response.success && response.data?.is_active) {
                  // Store tokens
                  this.tokenService.storeAuthData({
                    access_token: response.data.token?.access_token,
                    refresh_token: response.data.token?.refresh_token,
                    user_code: response.data.user_code,
                    expires_in: response.data.token?.expires_in,
                  });

                  // Store user data
                  const userData = JSON.stringify(response.data);
                  this.ssrcookieService.setItem('eol_user', userData);
                  this.localstorage.setItem('eol_user', userData);

                  // ⚠️ Consider avoiding OTP in localStorage (security risk)
                  localStorage.setItem(
                    'registrationPhoneNumber',
                    this.phoneNumber,
                  );
                  // UUID verified successfully - user is now logged in
                  this.authService.login(response.data);
                  this.authService.refreshAuthState();

                  // Sync local cart and wishlist
                  this.syncLocalStorageCart();
                  this.syncLocalWishlist();

                  // Handle redirect based on action type
                  if (
                    ['cart_payment', 'productDetail', 'buy_now'].includes(
                      this.actionType,
                    ) &&
                    response.data.first_name === null
                  ) {
                    this.router.navigateByUrl('/booking-page');
                  } else {
                    this.gotoCreateForm();
                  }

                  this.showLoginModal = false;
                  this.loginSuccess.emit();

                  // Update auth state
                } else {
                  // New user — store tokens from OTP if present (registration API may not return them)
                  if (response.data?.token?.access_token && response.data?.user_code) {
                    this.tokenService.storeAuthData({
                      access_token: response.data.token.access_token,
                      refresh_token: response.data.token.refresh_token,
                      user_code: response.data.user_code,
                      expires_in: response.data.token.expires_in,
                    });
                  }
                  this.gotoCreateForm();
                  this.cdr.detectChanges();
                }
              }
            },
            error: (error: any) => {
              this.ngZone.run(() => {
                this.loading = false;
                this.errorMessage =
                  error.message || 'Failed to verify OTP. Please try again.';
                // Update OTP input styles for mobile view
                if (this.isMobile) {
                  this.hasOtpError = true; // Set error flag
                  this.cdr.markForCheck();
                  this.cdr.detectChanges(); // Force change detection to update border color
                } else {
                  // Mark all OTP fields as touched to show validation errors immediately
                  this.otpControls.forEach((controlName) => {
                    this.otpForm.get(controlName)?.markAsTouched();
                  });
                }
                // Force change detection to ensure error message displays immediately
                this.cdr.detectChanges();
              });
              // this.otpForm.reset();
              // this.otpInputs.first?.nativeElement.focus();
            },
          }),
      );
      // next: (response: any) => {
      //   this.loading = false;
      //   if (response.success && response.data.is_active) {
      //   // Store tokens from login response first
      //   this.tokenService.storeAuthData({
      //     access_token: response?.data?.token['access_token'],
      //     refresh_token: response?.data?.token['refresh_token'],
      //     user_code: response?.data?.user_code,
      //     expires_in: response?.data?.token['expires_in']
      //   });

      //   // Store additional user data
      //   this.ssrcookieService.setItem('eol_user', JSON.stringify(response.data));
      //   this.localstorage.setItem('eol_user', JSON.stringify(response.data));

      //   // Store phone number and OTP for registration
      //   localStorage.setItem('registrationPhoneNumber', this.phoneNumber);
      //   localStorage.setItem('registrationOtp', otp);

      //   // Use auth service to update login state
      //   this.authService.login(response.data);

      //   // Force refresh auth state to ensure components get updated state
      //   setTimeout(() => {
      //     this.authService.refreshAuthState();
      //   }, 100);

      //   // Sync local storage cart items to server after successful login
      //   this.syncLocalStorageCart();
      //   // Only redirect if not logging in from cart or product detail
      //   if (this.actionType !== 'cart_payment' && this.actionType !== 'productDeytail'&& this.actionType !== 'buy_now') {
      //     this.router.navigateByUrl('/home');
      //   } else if (response.success && response.data.last_name){
      //     if (this.actionType === 'cart_payment' || this.actionType === 'productDeytail'|| this.actionType === 'buy_now') {
      //       this.router.navigateByUrl('/booking-page');
      //     }
      //   }
      //   this.showLoginModal = false;
      //   this.loginSuccess.emit();
      //   } else if(response.success && this.productData) {
      //     this.loginSuccess.emit();
      //     this.showLoginModal = false;
      //     // Only redirect if not logging in from cart
      //     if (this.actionType === 'cart_payment' || this.actionType === 'productDeytail'|| this.actionType === 'buy_now') {
      //       this.router.navigateByUrl('/booking-page');
      //     }
      //   } else {
      //     this.gotoCreateForm();
      //   }
      // },
      //   error: (error: any) => {
      //     this.loading = false;
      //     this.errorMessage = error.message || 'Failed to verify OTP. Please try again.';
      //     this.otpForm.reset();
      //     this.otpInputs.first?.nativeElement.focus();
      //   }
      // }));
    }
  }

  gotoCreateForm() {
    this.createForm = true;
    this.verifyOtp = false;
    this.showLoginModal = false;
    this.otpForm.reset();
    this.cdr.detectChanges();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  resendOtp() {
    if (this.remainingSeconds === 0 && !this.loading) {
      // Skip captcha check when resending OTP
      this.getOtp(true);
    }
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }



  closeModal() {
    this.modalService.closeModal('login-popup');
    this.showLoginModal = false;
    this.verifyOtp = false;
    this.createForm = false;
    this.verificationStep = null;
    this.loginSuccess.emit();
  }

  // --- Buyer verification (managed in login after register business step) ---

  loadVerificationStatus(): void {
    this.sub.add(
      this.buyerVerificationService.getStatus().subscribe({
        next: (data) => {
          if (this.buyerVerificationService.isFullyVerified(data)) {
            this.verificationStep = 'activated';
            this.buyerVerificationService.clearSkipped();
            return;
          }
          this.setLinkedDocuments(data.gstins);
          if (data.pan_verified && this.gstins.length) {
            if (data.route_verified) {
              this.legalName = data.legal_name ?? '';
              this.legalAddress = data.legal_address ?? '';
              this.verificationStatusState = 'verified';
              this.verificationStep = 'status';
            } else {
              this.verificationStep = 'gstin';
            }
            this.cdr.detectChanges();
            return;
          }
          if (
            data.pan_verified &&
            (this.buyerVerificationService.isShopLicenseRoute(data) ||
              this.udyamItems.length ||
              !!data.uam_number)
          ) {
            if (data.route_verified) {
              this.legalName = data.legal_name ?? '';
              this.legalAddress = data.legal_address ?? '';
              this.verificationStatusState = 'verified';
              this.verificationStep = 'status';
            } else {
              this.openShopLicenseStep({
                name: '',
                certificateNumber: data.shop_license_number,
                state: data.shop_license_state,
                uamNumber: data.uam_number,
              });
            }
            return;
          }
          this.openBusinessStep();
        },
        error: () => this.openBusinessStep(),
      }),
    );
  }

  private openBusinessStep(): void {
    this.createForm = true;
    this.registerBusinessOnly = true;
    this.verificationStep = null;
    this.cdr.detectChanges();
  }

  private openShopLicenseStep(options: {
    name?: string;
    certificateNumber?: string;
    state?: string;
    uamNumber?: string;
  } = {}): void {
    this.createForm = false;
    this.registerBusinessOnly = false;
    this.shopLicenseName = options.name ?? '';
    this.uamNumber = (
      options.uamNumber ||
      this.getUdyamNumber(this.udyamItems[0]) ||
      ''
    ).toUpperCase();
    this.shopLicenseNumber = options.certificateNumber ?? '';
    this.shopLicenseState = options.state?.trim().toLowerCase() ?? '';
    this.shopLicenseCommencementDate = '';
    this.shopLicenseDistrict = '';
    this.shopLicenseType =
      this.uamNumber || this.udyamItems.length ? 'uam' : 'certificate';
    this.shopLicenseErrorMessage = '';
    this.uamLookupErrorMessage = '';
    this.resetShopLicenseTouched();
    this.verificationStep = 'shop-license';
    if (!this.udyamItems.length) {
      this.fetchUdyamDocuments();
    }
    this.cdr.detectChanges();
  }

  private resetShopLicenseTouched(): void {
    this.uamNumberTouched = false;
    this.shopLicenseNumberTouched = false;
    this.shopLicenseStateTouched = false;
    this.shopLicenseCommencementDateTouched = false;
    this.shopLicenseDistrictTouched = false;
  }

  onShopLicenseTypeChange(): void {
    this.shopLicenseErrorMessage = '';
    this.uamLookupErrorMessage = '';
    this.resetShopLicenseTouched();

    if (this.shopLicenseType === 'uam') {
      this.shopLicenseNumber = '';
      this.shopLicenseState = '';
      this.shopLicenseCommencementDate = '';
      this.shopLicenseDistrict = '';
      if (!this.uamNumber.trim()) {
        this.uamNumber = this.getUdyamNumber(this.udyamItems[0]);
      }
      if (!this.udyamItems.length) {
        this.fetchUdyamDocuments();
      }
      return;
    }

    this.uamNumber = '';
  }

  onBusinessStepComplete(result: BusinessStepResult): void {
    this.createForm = false;
    this.registerBusinessOnly = false;

    if ('skipped' in result) {
      this.finishVerificationSkipped();
      return;
    }

    this.setLinkedDocuments(result.gstins);
    if (this.gstins.length) {
      this.verificationStep = 'gstin';
    } else {
      this.openShopLicenseStep({ name: result.businessFullName });
    }
    this.cdr.detectChanges();
  }

  private setLinkedDocuments(documents: GstinItem[] | null | undefined): void {
    this.linkedDocuments = documents ?? [];
    this.gstins = this.buyerVerificationService.filterGstDocuments(
      this.linkedDocuments,
    );
    this.udyamItems = this.buyerVerificationService.filterUdyamDocuments(
      this.linkedDocuments,
    );

    if (
      this.selectedGstin &&
      !this.gstins.some(
        (item) => this.getGstinNumber(item) === this.getGstinNumber(this.selectedGstin),
      )
    ) {
      this.selectedGstin = null;
    }

    if (!this.uamNumber.trim() && this.udyamItems.length === 1) {
      this.uamNumber = this.getUdyamNumber(this.udyamItems[0]);
    }
  }

  private mergeLinkedDocuments(documents: GstinItem[] | null | undefined): void {
    if (!documents?.length) return;

    const merged = [...this.linkedDocuments];
    documents.forEach((document) => {
      const documentNumber = this.getGstinNumber(document);
      const existingIndex = merged.findIndex(
        (item) =>
          this.getGstinNumber(item) === documentNumber &&
          item.document_type === document.document_type,
      );

      if (existingIndex >= 0) {
        merged[existingIndex] = document;
      } else {
        merged.push(document);
      }
    });
    this.setLinkedDocuments(merged);
  }

  isGstinActive(gstin: GstinItem): boolean {
    const row = gstin as unknown as Record<string, unknown>;
    const status = String(row['status'] || row['gstin_status'] || '')
      .trim()
      .toLowerCase();
    return status === 'active';
  }

  isGstinSelected(gstin: GstinItem): boolean {
    return this.getGstinNumber(this.selectedGstin) === this.getGstinNumber(gstin);
  }

  private getGstinNumber(item: GstinItem | null | undefined): string {
    const row = (item || {}) as unknown as Record<string, unknown>;
    return String(row['document_number'] || row['gstin'] || '').trim();
  }

  getUdyamNumber(item: GstinItem | null | undefined): string {
    return this.getGstinNumber(item).toUpperCase();
  }

  get selectedUdyamItem(): GstinItem | null {
    const selected = this.uamNumber.trim().toUpperCase();
    if (!selected) return null;
    return (
      this.udyamItems.find(
        (item) => this.getUdyamNumber(item) === selected,
      ) ?? null
    );
  }

  private fetchUdyamDocuments(): void {
    if (this.uamLookupLoading) return;
    this.uamLookupLoading = true;
    this.uamLookupErrorMessage = '';

    this.sub.add(
      this.buyerVerificationService.fetchUdyam().subscribe({
        next: (data) => {
          this.uamLookupLoading = false;
          this.mergeLinkedDocuments(data.gstins);
          const fetchedUamNumber =
            data.uam_number || this.getUdyamNumber(this.udyamItems[0]);
          if (fetchedUamNumber && !this.uamNumber.trim()) {
            this.uamNumber = fetchedUamNumber.toUpperCase();
            this.shopLicenseType = 'uam';
          }
          this.cdr.detectChanges();
        },
        error: (error: Error) => {
          this.uamLookupLoading = false;
          this.uamLookupErrorMessage = error.message;
          this.cdr.detectChanges();
        },
      }),
    );
  }

  onGstinSelect(gstin: GstinItem): void {
    if (!this.isGstinActive(gstin)) return;
    this.selectedGstin = gstin;
    this.gstinErrorMessage = '';
    this.cdr.detectChanges();
  }

  onGstinSubmit(): void {
    const gstinNumber = this.getGstinNumber(this.selectedGstin);
    if (!gstinNumber || this.gstinLoading) return;
    this.gstinLoading = true;
    this.gstinErrorMessage = '';

    this.sub.add(
      this.buyerVerificationService
        .selectGstin(gstinNumber)
        .subscribe({
          next: (data) => {
            this.gstinLoading = false;
            this.legalName = data.legal_name ?? '';
            this.legalAddress = data.legal_address ?? '';
            this.verificationStatusState = 'verified';
            this.verificationStep = 'status';
            this.buyerVerificationService.clearSkipped();
            this.cdr.detectChanges();
          },
          error: (error: Error) => {
            this.gstinLoading = false;
            this.gstinErrorMessage = error.message;
            this.cdr.detectChanges();
          },
        }),
    );
  }

  onVerificationStatusContinue(): void {
    this.verificationStep = 'activated';
    this.cdr.detectChanges();
  }

  onVerificationStatusRetry(): void {
    this.verificationStatusState = 'verifying';
    this.verificationStatusMessage = '';

    if (this.verificationRetryStep === 'shop-license') {
      this.verificationStep = 'shop-license';
    } else if (this.verificationRetryStep === 'gstin') {
      this.verificationStep = 'gstin';
    } else {
      this.openBusinessStep();
    }
    this.cdr.detectChanges();
  }

  isShopLicenseValid(): boolean {
    if (this.shopLicenseType === 'uam') {
      return !!this.uamNumber.trim();
    }

    return (
      !!this.shopLicenseNumber.trim() &&
      !!this.shopLicenseState.trim() &&
      !!this.shopLicenseCommencementDate &&
      !!this.shopLicenseDistrict.trim()
    );
  }

  getUamNumberError(): string {
    if (this.shopLicenseType !== 'uam' || !this.uamNumberTouched) return '';
    if (!this.uamNumber.trim()) {
      return this.udyamItems.length
        ? 'Select a UAM number'
        : 'UAM number is required';
    }
    return '';
  }

  getShopLicenseNumberError(): string {
    if (this.shopLicenseType !== 'certificate' || !this.shopLicenseNumberTouched) {
      return '';
    }
    if (!this.shopLicenseNumber.trim()) {
      return 'Certificate number is required';
    }
    return '';
  }

  getShopLicenseStateError(): string {
    if (this.shopLicenseType !== 'certificate' || !this.shopLicenseStateTouched) {
      return '';
    }
    if (!this.shopLicenseState.trim()) return 'State is required';
    return '';
  }

  getShopLicenseCommencementDateError(): string {
    if (
      this.shopLicenseType !== 'certificate' ||
      !this.shopLicenseCommencementDateTouched
    ) {
      return '';
    }
    if (!this.shopLicenseCommencementDate) {
      return 'Commencement date is required';
    }
    return '';
  }

  getShopLicenseDistrictError(): string {
    if (this.shopLicenseType !== 'certificate' || !this.shopLicenseDistrictTouched) {
      return '';
    }
    if (!this.shopLicenseDistrict.trim()) return 'District is required';
    return '';
  }

  formatShopLicenseStateLabel(state: string): string {
    return state.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  onShopLicenseSubmit(): void {
    this.shopLicenseErrorMessage = '';

    if (this.shopLicenseType === 'uam') {
      this.uamNumberTouched = true;
    } else {
      this.shopLicenseNumberTouched = true;
      this.shopLicenseStateTouched = true;
      this.shopLicenseCommencementDateTouched = true;
      this.shopLicenseDistrictTouched = true;
    }

    if (!this.isShopLicenseValid() || this.shopLicenseLoading) return;

    this.shopLicenseLoading = true;

    const request$ =
      this.shopLicenseType === 'uam'
        ? this.buyerVerificationService.verifyUam({
            uam_number: this.uamNumber.trim(),
          })
        : this.buyerVerificationService.verifyShopLicense({
            certificate_number: this.shopLicenseNumber.trim(),
            state: this.shopLicenseState.trim().toLowerCase(),
            name: this.shopLicenseName.trim(),
            commencement_date: this.shopLicenseCommencementDate,
            district: this.shopLicenseDistrict.trim(),
          });

    this.sub.add(
      request$.subscribe({
        next: (data) => {
          this.shopLicenseLoading = false;
          this.legalName =
            data.legal_name ??
            this.shopLicenseName.trim() ??
            this.uamNumber.trim();
          this.legalAddress = data.legal_address ?? '';
          this.verificationStatusState = 'verified';
          this.verificationRetryStep = 'shop-license';
          this.verificationStep = 'status';
          this.buyerVerificationService.clearSkipped();
          this.cdr.detectChanges();
        },
        error: (error: Error) => {
          this.shopLicenseLoading = false;
          this.verificationStatusState = 'failed';
          this.verificationStatusMessage = error.message;
          this.verificationRetryStep = 'shop-license';
          this.verificationStep = 'status';
          this.cdr.detectChanges();
        },
      }),
    );
  }

  onAccountActivated(): void {
    this.verificationStep = null;
    if (this.verificationCheckoutMode) {
      this.verificationComplete.emit();
      this.buyerVerificationService.completeCheckoutVerification();
      return;
    }
    this.loginSuccess.emit();
    this.router.navigate(['/home']);
  }

  finishVerificationSkipped(): void {
    this.verificationStep = null;
    this.createForm = false;
    if (this.verificationCheckoutMode) {
      this.buyerVerificationService.cancelCheckoutVerification();
      return;
    }
    this.loginSuccess.emit();
    this.router.navigate(['/home']);
  }

  onRegisterClose(): void {
    if (this.verificationCheckoutMode) {
      this.buyerVerificationService.cancelCheckoutVerification();
      return;
    }
    this.loginSuccess.emit();
  }

  get verificationLeftPanelTitle(): string {
    switch (this.verificationStep) {
      case 'gstin':
        return 'Choose the GSTIN for this account';
      case 'shop-license':
        return 'Verify with Shop License';
      case 'status':
        if (this.verificationStatusState === 'verified') return 'Verified instantly';
        if (this.verificationStatusState === 'failed') return "We couldn't verify that";
        return 'Verifying your details';
      case 'activated':
        return "You're all set";
      default:
        return '';
    }
  }

  get verificationLeftPanelSubtext(): string {
    switch (this.verificationStep) {
      case 'gstin':
        return `We found ${this.gstins.length} linked to this PAN`;
      case 'shop-license':
        return 'No GSTINs were linked to your PAN. Verify using UAM or shop license certificate.';
      case 'status':
        if (this.verificationStatusState === 'verified') {
          return 'Your business details have been confirmed.';
        }
        if (this.verificationStatusState === 'failed') {
          return 'Please review the details and try again.';
        }
        return 'This usually takes just a few seconds.';
      case 'activated':
        return 'Ready to browse and order';
      default:
        return '';
    }
  }

  get verificationStatusDetail(): string {
    if (this.verificationStatusState === 'verified') {
      const parts = [this.legalName, this.legalAddress].filter(Boolean);
      return parts.join(' · ') || 'Your business has been verified successfully.';
    }
    return (
      this.verificationStatusMessage ||
      'Something went wrong during verification.'
    );
  }

  // Method to sync local storage cart items to server after login
  private syncLocalStorageCart(): void {
    if (this.cartService && this.cartService.hasLocalStorageItems()) {
      this.cartService.syncLocalStorageToServer().subscribe({
        next: (res: any) => {},
        error: (err: any) => {
          console.error('Error syncing local storage cart items:', err);
        },
      });
    }
  }

  /**
   * Sync localStorage wishlist items to server after login
   */
  private syncLocalWishlist(): void {
    const wishlistItems = this.wishlistStorageService.getWishlistItemsForSync();

    if (wishlistItems && wishlistItems.length > 0) {
      const payload = {
        action_type: 'add_or_update',
        wishlist_items: wishlistItems,
      };

      this.sub.add(
        this.apiService.addToWishlist(payload).subscribe({
          next: (res: any) => {
            // Clear localStorage wishlist after successful sync
            this.wishlistStorageService.clearLocalWishlist();
          },
          error: (err: any) => {
            console.error('Error syncing local wishlist items:', err);
            // Don't clear localStorage if sync fails - user can try again
          },
        }),
      );
    }
  }

  /**
   * Check if phone number field should show validation errors
   * Shows errors only when user has typed something (dirty) OR when touched with a value
   * This prevents errors from showing when Turnstile steals focus before user types
   */
  shouldShowPhoneError(): boolean {
    const phoneControl = this.loginForm.get('phoneNumber');
    if (!phoneControl) return false;

    const isDirty = phoneControl.dirty;
    const isTouched = phoneControl.touched;
    const hasValue = phoneControl.value && phoneControl.value.length > 0;
    const isInvalid = phoneControl.invalid;

    // Show error if: (user typed something AND invalid) OR (touched with value AND invalid)
    return (isDirty || (isTouched && hasValue)) && isInvalid;
  }

  proceedWithLoginBypass(uuid: string) {
    const payload = {
      uuid: uuid,
    };
    // Verify UUID and proceed with login
    this.apiService.loginByPass(payload).subscribe({
      next: (response: any) => {
        if (response.success) {
          // UUID verified successfully - user is now logged in
          this.authService.login(response.data);
          this.authService.refreshAuthState();

          // Sync local cart and wishlist
          this.syncLocalStorageCart();
          this.syncLocalWishlist();

          // Handle redirect based on action type
          if (
            ['cart_payment', 'productDetail', 'buy_now'].includes(
              this.actionType,
            ) &&
            response.data.first_name === null
          ) {
            this.router.navigateByUrl('/booking-page');
          } else {
            this.gotoCreateForm();
          }

          this.showLoginModal = false;
          this.loginSuccess.emit();

          // Note: Token storage and auth state should already be handled
          // by the verifyOtp method before this bypass call
        } else {
          console.error('❌ Login bypass failed: Invalid response');
          this.errorMessage = 'Login verification failed. Please try again.';
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('❌ Login bypass failed:', error);
        this.errorMessage =
          error.message || 'Login verification failed. Please try again.';
        this.cdr.detectChanges();
      },
    });
  }
}
